import React, { useRef } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Stack,
  Fab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import AddressSearch from '../../AddressSearch';

const ContactDetailsStep = ({
  formData,
  handleInputChange,
  selectedAddress,
  setSelectedAddress,
  onBack,
  onSubmit,
  loading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const submitButtonRef = useRef(null);

  const mobileTextFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: 'white',
      fontSize: '0.9rem',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.2)',
      },
      '&:hover fieldset': {
        borderColor: '#f5f5dc',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#f5f5dc',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.7)',
      fontSize: '0.9rem',
      '&.Mui-focused': {
        color: '#f5f5dc',
      },
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255,255,255,0.5)',
      fontSize: '0.75rem',
    },
  };

  const desktopTextFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.05)',
      color: 'white',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.2)',
      },
      '&:hover fieldset': {
        borderColor: '#f5f5dc',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#f5f5dc',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.7)',
      '&.Mui-focused': {
        color: '#f5f5dc',
      },
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255,255,255,0.5)',
    },
  };

  const textFieldStyles = isMobile ? mobileTextFieldStyles : desktopTextFieldStyles;

  if (isMobile) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            mb: 3, 
            color: 'white',
            fontSize: '1.1rem',
            textAlign: 'center'
          }}
        >
          Vos coordonnées
        </Typography>
        
        <Box sx={{ flex: 1, mb: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Gérant de l'établissement*"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleInputChange}
              placeholder="Ex : Antoine Martin"
              required
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />
            
            <TextField
              fullWidth
              label="Nom de l'établissement*"
              name="salonName"
              value={formData.salonName}
              onChange={handleInputChange}
              placeholder="Ex : Salon Antoine"
              required
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />
            
            <TextField
              fullWidth
              label="Téléphone*"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Ex : 06 86 26 44 44"
              required
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />
            
            <TextField
              fullWidth
              label="Email*"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Ex : antoine.martin@gmail.com"
              required
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />

            <TextField
              fullWidth
              label="Mot de passe*"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              helperText="Minimum 6 caractères"
              required
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />

            <TextField
              fullWidth
              label="Confirmer le mot de passe*"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />

            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1, fontSize: '0.85rem' }}>
                Adresse complète *
              </Typography>
              <AddressSearch
                value={selectedAddress ? selectedAddress.query : ''}
                onChange={setSelectedAddress}
                placeholder="Rechercher votre adresse complète..."
                countries={['fr', 'be', 'ch', 'lu', 'ca']} // France, Belgique, Suisse, Luxembourg, Canada
                theme="dark"
              />
            </Box>
          </Stack>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                borderColor: '#f5f5dc',
                backgroundColor: 'rgba(245,245,220,0.1)',
                color: '#f5f5dc'
              }
            }}
          >
            Retour
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={loading}
            sx={{ 
              flex: 2,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.9rem',
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              color: '#212121',
              boxShadow: '0 4px 15px rgba(245, 245, 220, 0.3)',
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            {loading ? 'Création...' : 'Valider'}
          </Button>
        </Box>

        <Box textAlign="center" mt={2}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
            <Box
              component="a"
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                textDecoration: 'none',
                '&:hover': {
                  color: '#f5f5dc',
                  textDecoration: 'underline'
                }
              }}
            >
              En savoir plus sur la collecte de vos données personnelles
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  }

  // Desktop version
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white' }}>
        Pourriez-vous nous fournir vos coordonnées ?
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Gérant de l'établissement*"
            name="ownerName"
            value={formData.ownerName}
            onChange={handleInputChange}
            placeholder="Ex : Antoine Martin"
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nom de l'établissement*"
            name="salonName"
            value={formData.salonName}
            onChange={handleInputChange}
            placeholder="Ex : Salon Antoine"
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Numéro de téléphone*"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Ex : 06 86 26 44 44"
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email*"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Ex : antoine.martin@gmail.com"
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Mot de passe*"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            helperText="Minimum 6 caractères"
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Confirmer le mot de passe*"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
            Adresse complète *
          </Typography>
          <Box sx={textFieldStyles}>
            <AddressSearch
              value={selectedAddress ? selectedAddress.query : ''}
              onChange={setSelectedAddress}
              placeholder="Rechercher votre adresse complète..."
              countries={['fr', 'be', 'ch', 'lu', 'ca']} // France, Belgique, Suisse, Luxembourg, Canada
              theme="dark"
            />
          </Box>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#f5f5dc'
            }
          }}
        >
          Retour
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading}
          sx={{ 
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
            color: '#212121',
            boxShadow: '0 8px 25px rgba(245, 245, 220, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(245, 245, 220, 0.4)'
            },
            '&:disabled': {
              background: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.3)'
            }
          }}
        >
          {loading ? 'Création...' : 'Valider'}
        </Button>
      </Box>

      <Box textAlign="center" mt={3}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <Box
            component="a"
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textDecoration: 'none',
              '&:hover': {
                color: '#f5f5dc',
                textDecoration: 'underline'
              }
            }}
          >
            En savoir plus sur la collecte de vos données personnelles
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default ContactDetailsStep;